import moment from 'moment'

/**
 * A timestamp as "05 Jun 2024, 09:07 AM".
 *
 * `padHour: false` drops the leading zero — "05 Jun 2024, 9:07 AM" — which is
 * pitstop's `DD MMM YYYY, h:mm A`. The day stays padded either way: it reads as
 * a date part, where a clock is not written "03:07 PM".
 *
 * Padded by default because that is what the Environmental Monthly Submissions
 * detail page renders in production, and this default is what keeps it there.
 * The pages ported from pitstop opt out.
 */
export const formatDate = (timestamp, { padHour = true } = {}) => {
  const d = new Date(timestamp)
  const day = d.getDate().toString().padStart(2, '0')
  const month = d.toLocaleString('en-GB', { month: 'short' })
  const year = d.getFullYear()
  const hours = d.getHours()
  const mins = d.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 || 12
  const h = padHour ? hour12.toString().padStart(2, '0') : hour12
  return `${day} ${month} ${year}, ${h}:${mins} ${ampm}`
}

/**
 * How every submittals surface writes a full timestamp.
 *
 * `formatDate` above is the other one — "20 Aug 2026, 1:05 AM", which the detail
 * header's "Last updated on" line uses. This is the form the configured listing's
 * date columns and the detail header's Submission Date read in: "20/08/2026
 * 12:58am". Both live here so neither surface can drift from the other, which is
 * how pitstop keeps them (src/pages/submittals/utils/format-date.js).
 */
export const SUBMITTAL_DATE_TIME_FORMAT = 'DD/MM/YYYY h:mma'

/**
 * A stored timestamp in the viewer's own zone — moment parses the `Z` Go sends
 * and formats local.
 *
 * Deliberately lenient: an unparseable value is returned as it came rather than
 * becoming "Invalid date", so an already-formatted string passes straight
 * through. It does NOT decide whether a value IS a timestamp — `moment('2026-05')`
 * is valid and would come back as midnight on the 1st. Callers establish that
 * first: the listing from the column type its config declares, the detail header
 * from the column the service promotes.
 */
export const formatDateTime = value => {
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format(SUBMITTAL_DATE_TIME_FORMAT) : String(value)
}
